import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  useEffect(() => {
    fetchBookingDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBookingDetail = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBooking(data.booking);
      } else {
        setError("Failed to load booking details");
      }
    } catch (err) {
      setError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    let date;
    if (dateString.includes("T")) {
      date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      date = new Date(year, month, day);
    } else {
      const [year, month, day] = dateString.split("-").map(Number);
      date = new Date(year, month - 1, day);
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleCancelBooking = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/${id}/cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: cancelReason }),
        },
      );

      if (response.ok) {
        alert("Booking cancelled successfully");
        setShowCancelModal(false);
        fetchBookingDetail();
      } else {
        const data = await response.json();
        alert(data.message || "Failed to cancel booking");
      }
    } catch (err) {
      alert("Failed to cancel booking");
    }
  };

  const handleCompleteBooking = async () => {
    if (!window.confirm("Mark this booking as completed?")) return;

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/${id}/complete`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        alert("Booking marked as completed");
        fetchBookingDetail();
      } else {
        const data = await response.json();
        alert(data.message || "Failed to complete booking");
      }
    } catch (err) {
      alert("Failed to complete booking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "completed":
        return { bg: "#d1fae5", color: "#065f46" };
      case "cancelled":
        return { bg: "#fee2e2", color: "#991b1b" };
      case "pending":
        return { bg: "#fef3c7", color: "#92400e" };
      default:
        return { bg: "#f3f4f6", color: "#374151" };
    }
  };

  const getCancellationPayoutAmount = () => {
    if (!booking) return null;
    if (booking.status !== "cancelled" && booking.status !== "canceled") {
      return null;
    }

    // The canonical figure when the API supplies it...
    const canonical = Number(booking.cancellationPayoutAmount) || 0;
    if (canonical > 0) return canonical;

    const distribution = booking.cancellationFeeDistribution;
    if (!distribution) return null;

    // ...otherwise the professional's shares ADDED, which is how the payout calculator reads
    // them (utils/paymentSplitCalculator.js). ownerShare is deliberately excluded: it is the
    // BUSINESS's share, and showing it as the professional's payout is how this screen could
    // disagree with what they are actually paid.
    const payoutAmount =
      (Number(distribution.professionalShare) || 0) +
      (Number(distribution.freelancerShare) || 0);

    return payoutAmount > 0 ? payoutAmount : null;
  };

  if (loading) {
    return (
      <StaffSidebarLayout>
        <div
          style={{ padding: isMobile ? "16px" : "24px", textAlign: "center" }}
        >
          <p>Loading booking details...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  if (error || !booking) {
    return (
      <StaffSidebarLayout>
        <div style={{ padding: isMobile ? "16px" : "24px" }}>
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #dc2626",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <p style={{ color: "#991b1b", margin: 0 }}>
              {error || "Booking not found"}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "8px 16px",
              background: "#6b7280",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Go Back
          </button>
        </div>
      </StaffSidebarLayout>
    );
  }

  const statusStyle = getStatusColor(booking.status);
  const displayAmount = getCancellationPayoutAmount();

  return (
    <StaffSidebarLayout>
      <div
        style={{
          padding: isMobile ? "16px" : "24px",
          maxWidth: "1000px",
          margin: "0 auto",
          paddingBottom: isMobile ? "80px" : "40px",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              color: "#2563eb",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            ← Back
          </button>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "28px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                Booking Details
              </h1>
              <div
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  background: statusStyle.bg,
                  color: statusStyle.color,
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {booking.status.charAt(0).toUpperCase() +
                  booking.status.slice(1)}
              </div>
            </div>
            {booking.status === "confirmed" && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  onClick={handleCompleteBooking}
                  style={{
                    padding: "8px 16px",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Mark Complete
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  style={{
                    padding: "8px 16px",
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Cancel Booking
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Appointment Details */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "20px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Appointment Information
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Service
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {booking.serviceName || "N/A"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Duration
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {booking.duration} minutes
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Date
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {formatDate(booking.appointmentDate)}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Time
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "20px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Customer Information
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Name
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {booking.customerName}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Email
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {booking.customerEmail}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                Phone
              </div>
              <div style={{ fontSize: "14px", fontWeight: "500" }}>
                {booking.customerPhone}
              </div>
            </div>
          </div>
        </div>

        {/* Products */}
        {booking.products && booking.products.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "16px" : "20px",
              marginBottom: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Products
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {booking.products.map((product, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      {product.productName}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Quantity: {product.quantity}
                    </div>
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "600" }}>
                    ${(product.priceAtBooking * product.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Summary */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "20px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Payment Summary
          </h2>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                {displayAmount !== null ? "Cancellation Payout" : "Service Total"}
              </span>
              <span style={{ fontSize: "14px", fontWeight: "500" }}>
                ${
                  displayAmount !== null
                    ? displayAmount.toFixed(2)
                    : booking.servicePriceAtBooking?.toFixed(2)
                }
              </span>
            </div>
            {displayAmount === null && booking.productsTotalPrice > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Products Total
                </span>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  ${booking.productsTotalPrice.toFixed(2)}
                </span>
              </div>
            )}
            {displayAmount === null && booking.tipAmount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Tip{booking.tipPercentage > 0 ? ` (${booking.tipPercentage}%)` : ""}
                </span>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  ${booking.tipAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div
              style={{
                borderTop: "2px solid #e5e7eb",
                paddingTop: "12px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: "16px", fontWeight: "600" }}>Total</span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#2563eb",
                }}
              >
                $
                {(
                  displayAmount !== null
                    ? displayAmount
                    : (booking.servicePriceAtBooking || 0) +
                      (booking.productsTotalPrice || 0) +
                      (booking.tipAmount || 0)
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <>
          <div
            onClick={() => setShowCancelModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 999,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              zIndex: 1000,
              maxWidth: isMobile ? "90%" : "500px",
              width: "100%",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Cancel Booking
            </h2>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="4"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
                placeholder="Provide a reason..."
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
              <button
                onClick={handleCancelBooking}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </>
      )}
    </StaffSidebarLayout>
  );
};

export default BookingDetail;
